import { useState, useEffect } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL

/* Hook simple para detectar si estamos en tamano movil */
function useEsMovil() {
  const [esMovil, setEsMovil] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 820 : false
  )
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 820px)')
    const handler = e => setEsMovil(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return esMovil
}

// Misma escala CEFR y los mismos umbrales de XP que calcula el backend
// en calcularNivel() (api/src/routes/progreso.js y otras rutas que dan
// XP) — antes esta pantalla tenia su propia escala inventada
// ("Principiante/Basico/Intermedio/Avanzado/Experto" con otros
// numeros), que no coincidia con el nivel real guardado en la base de
// datos y que se muestra en la pantalla de Progreso. Los mismos colores
// que usa Progreso.jsx, para que se vea igual en toda la plataforma.
const NIVEL_COLORES = { PRE: '#94A3B8', A1: '#22C55E', A2: '#3B82F6', B1: '#8B5CF6', B2: '#F59E0B', C1: '#EF4444', C2: '#1B3A8C' }

function getNivel(xp) {
  if (xp < 800)   return { nombre: 'PRE', color: NIVEL_COLORES.PRE, siguiente: 800 }
  if (xp < 2500)  return { nombre: 'A1',  color: NIVEL_COLORES.A1,  siguiente: 2500 }
  if (xp < 5000)  return { nombre: 'A2',  color: NIVEL_COLORES.A2,  siguiente: 5000 }
  if (xp < 8000)  return { nombre: 'B1',  color: NIVEL_COLORES.B1,  siguiente: 8000 }
  if (xp < 12000) return { nombre: 'B2',  color: NIVEL_COLORES.B2,  siguiente: 12000 }
  return           { nombre: 'C1',  color: NIVEL_COLORES.C1,  siguiente: 12000 }
}

export default function Curriculo({ usuario, onIniciarLeccion }) {
  const [niveles,      setNiveles]      = useState([])
  const [nivelActivo,  setNivelActivo]  = useState(null)
  const [unidades,     setUnidades]     = useState([])
  const [unidadActiva, setUnidadActiva] = useState(null)
  const [lecciones,    setLecciones]    = useState([])
  const [progreso,     setProgreso]     = useState([])
  const [estadisticas, setEstadisticas] = useState({ xp_total: 0, lecciones_completadas: 0 })
  const [cargando,     setCargando]     = useState(true)
  const [examenesAprobados, setExamenesAprobados] = useState({})
  const esMovil = useEsMovil()

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/curriculo/niveles`),
      usuario
        ? axios.get(`${API}/progreso/usuario/${usuario.id}`).catch(() => ({ data: { progreso_lecciones: [], estadisticas: { xp_total: 0, lecciones_completadas: 0 } } }))
        : Promise.resolve({ data: { progreso_lecciones: [], estadisticas: { xp_total: 0, lecciones_completadas: 0 } } })
    ]).then(([niv, prog]) => {
      setNiveles(niv.data.datos || [])
      setProgreso(prog.data.progreso_lecciones || [])
      setEstadisticas(prog.data.estadisticas || { xp_total: 0, lecciones_completadas: 0 })
      if (niv.data.datos?.length > 0) setNivelActivo(niv.data.datos[0])
    }).finally(() => setCargando(false))
  }, [usuario])

  useEffect(() => {
    if (!nivelActivo) return
    setUnidades([]); setUnidadActiva(null); setLecciones([])
    axios.get(`${API}/curriculo/niveles/${nivelActivo.id}/unidades`)
      .then(r => {
        setUnidades(r.data.datos || [])
        if (r.data.datos?.length > 0) setUnidadActiva(r.data.datos[0])
      })
  }, [nivelActivo])

  useEffect(() => {
    if (!unidadActiva) return
    setLecciones([])
    axios.get(`${API}/curriculo/unidades/${unidadActiva.id}/lecciones`)
      .then(r => setLecciones(r.data.datos || []))

    if (usuario?.id) {
      axios.get(`${API}/curriculo/unidades/${unidadActiva.id}/examen/resultado/${usuario.id}`)
        .then(r => {
          if (r.data.aprobado) {
            setExamenesAprobados(prev => ({ ...prev, [unidadActiva.id]: true }))
          }
        })
        .catch(() => {})
    }
  }, [unidadActiva, usuario])

  function leccionCompletada(leccionId) {
    return progreso.some(p => p.leccion_id === leccionId && p.estado === 'completada')
  }

  function xpLeccion(leccionId) {
    const p = progreso.find(p => p.leccion_id === leccionId)
    return p?.xp_ganado || 0
  }

  function leccionDesbloqueada(lecciones, index) {
    if (index === 0) return true
    return leccionCompletada(lecciones[index - 1]?.id)
  }

  function handleIniciarLeccion(leccion, index) {
    if (!onIniciarLeccion) return
    const esUltima = index === lecciones.length - 1
    onIniciarLeccion(leccion.id, {
      unidadId:       unidadActiva?.id,
      unidadNombre:   unidadActiva?.titulo,
      esUltimaLeccion: esUltima
    })
  }

  const nivelInfo = getNivel(estadisticas.xp_total)
  const pctNivel = Math.min(100, Math.round((estadisticas.xp_total / nivelInfo.siguiente) * 100))

  if (cargando) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:60, gap:16, color:'#6B7280' }}>
      <div style={{ width:32, height:32, border:'3px solid #E2E6F0', borderTopColor:'#1B3A8C', borderRadius:'50%', animation:'spinner .8s linear infinite' }} />
      Cargando curriculo...
    </div>
  )

  return (
    <div style={{ maxWidth:900, margin:'0 auto' }}>

      <div style={{ background:'linear-gradient(135deg,#0D1F52,#1B3A8C)', borderRadius:16, padding:24, marginBottom:28, color:'#fff', display:'flex', alignItems:'center', gap:24, flexWrap:'wrap' }}>
        <div style={{ flex:1, minWidth:200 }}>
          <div style={{ fontSize:12, opacity:.7, marginBottom:4, textTransform:'uppercase', letterSpacing:1 }}>Tu nivel</div>
          <div style={{ fontSize:22, fontWeight:800 }}>{nivelInfo.nombre}</div>
          <div style={{ marginTop:8, background:'rgba(255,255,255,0.15)', borderRadius:999, height:6, overflow:'hidden' }}>
            <div style={{ width:`${pctNivel}%`, height:'100%', background: nivelInfo.color, borderRadius:999, transition:'width 1s ease' }} />
          </div>
          <div style={{ fontSize:11, opacity:.6, marginTop:4 }}>{estadisticas.xp_total} / {nivelInfo.siguiente} XP</div>
        </div>
        <div style={{ display:'flex', gap:20 }}>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:28, fontWeight:800, color:'#D4AF37' }}>⚡{estadisticas.xp_total}</div>
            <div style={{ fontSize:11, opacity:.7 }}>XP Total</div>
          </div>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:28, fontWeight:800 }}>📚{estadisticas.lecciones_completadas}</div>
            <div style={{ fontSize:11, opacity:.7 }}>Lecciones</div>
          </div>
        </div>
      </div>

      <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:20 }}>
        {niveles.map(n => (
          <button key={n.id} onClick={() => setNivelActivo(n)}
            style={{ padding:'8px 18px', borderRadius:999, border:`2px solid ${nivelActivo?.id===n.id ? '#1B3A8C' : '#E2E6F0'}`, background: nivelActivo?.id===n.id ? '#1B3A8C' : '#fff', color: nivelActivo?.id===n.id ? '#fff' : '#374151', fontFamily:'inherit', fontSize:13, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
            {n.nombre}
          </button>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns: esMovil ? '1fr' : '220px 1fr', gap:20 }}>
        <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:16, height:'fit-content' }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', marginBottom:12, textTransform:'uppercase', letterSpacing:1 }}>Units</div>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            {unidades.map((u, i) => {
              const leccCompletadas = progreso.filter(p =>
                lecciones.some(l => l.id === p.leccion_id) && p.estado === 'completada'
              ).length
              const pct = lecciones.length > 0 ? Math.round((leccCompletadas / lecciones.length) * 100) : 0
              const examenAprobado = examenesAprobados[u.id]
              return (
                <button key={u.id} onClick={() => setUnidadActiva(u)}
                  style={{ display:'flex', flexDirection:'column', padding:'10px 12px', borderRadius:10, border:`1.5px solid ${unidadActiva?.id===u.id ? '#1B3A8C' : 'transparent'}`, background: unidadActiva?.id===u.id ? '#E8EFFE' : 'transparent', cursor:'pointer', textAlign:'left', gap:4 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ fontSize:11, fontWeight:700, color:'#D4AF37' }}>U{i+1}</span>
                    <span style={{ fontSize:12, fontWeight:600, color:'#1A1A2E', flex:1 }}>{u.titulo}</span>
                    {examenAprobado && <span style={{ fontSize:12 }}>✅</span>}
                  </div>
                  {pct > 0 && (
                    <div style={{ background:'#E2E6F0', borderRadius:999, height:3, overflow:'hidden' }}>
                      <div style={{ width:`${pct}%`, height:'100%', background: pct===100 ? '#22C55E' : '#1B3A8C', borderRadius:999 }} />
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        <div>
          {unidadActiva && (
            <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:20 }}>
              <div style={{ marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <h2 style={{ fontSize:18, fontWeight:700, color:'#1B3A8C' }}>{unidadActiva.titulo}</h2>
                  {unidadActiva.descripcion && <p style={{ fontSize:13, color:'#6B7280', marginTop:4 }}>{unidadActiva.descripcion}</p>}
                </div>
                {examenesAprobados[unidadActiva.id] && (
                  <div style={{ background:'#F0FDF4', border:'1px solid #22C55E', borderRadius:10, padding:'6px 14px', fontSize:12, fontWeight:700, color:'#166534' }}>
                    ✅ Unit exam passed
                  </div>
                )}
              </div>

              {lecciones.length === 0 ? (
                <div style={{ textAlign:'center', padding:'40px 20px', color:'#6B7280' }}>
                  <div style={{ fontSize:36, marginBottom:10 }}>🚧</div>
                  <p style={{ fontSize:14, fontWeight:600, marginBottom:4 }}>Esta unidad esta en construccion</p>
                  <p style={{ fontSize:13 }}>Muy pronto vas a poder tomar las lecciones de "{unidadActiva.titulo}" aqui.</p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {lecciones.map((l, i) => {
                    const completada = leccionCompletada(l.id)
                    const desbloqueada = leccionDesbloqueada(lecciones, i)
                    const xpGanado = xpLeccion(l.id)
                    const esUltima = i === lecciones.length - 1
                    return (
                      <div key={l.id}
                        style={{ display:'flex', alignItems:'center', gap:16, padding:'14px 18px', border:`1.5px solid ${completada ? '#22C55E' : desbloqueada ? '#E2E6F0' : '#F3F4F6'}`, borderRadius:12, background: completada ? '#F0FDF4' : desbloqueada ? '#fff' : '#F9FAFB', opacity: desbloqueada ? 1 : 0.6 }}>
                        <div style={{ width:36, height:36, borderRadius:'50%', background: completada ? '#22C55E' : desbloqueada ? '#E8EFFE' : '#E2E6F0', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0, fontWeight:700, color: completada ? '#fff' : '#6B7280' }}>
                          {completada ? '✓' : desbloqueada ? i+1 : '🔒'}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontSize:14, fontWeight:600, color: desbloqueada ? '#1A1A2E' : '#9CA3AF' }}>{l.titulo}</span>
                            {esUltima && <span style={{ fontSize:10, background:'#FBF3D9', color:'#8A6D1D', borderRadius:999, padding:'2px 8px', fontWeight:700 }}>EXAM</span>}
                          </div>
                          <div style={{ display:'flex', gap:12, marginTop:3 }}>
                            <span style={{ fontSize:12, color:'#6B7280' }}>⏱ {l.duracion_min || 25} min</span>
                            {completada
                              ? <span style={{ fontSize:12, color:'#22C55E', fontWeight:600 }}>⚡ +{xpGanado} XP</span>
                              : <span style={{ fontSize:12, color:'#D4AF37' }}>⚡ {l.xp_total || 90} XP</span>
                            }
                          </div>
                        </div>
                        {desbloqueada && (
                          <button onClick={() => handleIniciarLeccion(l, i)}
                            style={{ background: completada ? '#E8EFFE' : '#1B3A8C', color: completada ? '#1B3A8C' : '#fff', border:'none', borderRadius:10, padding:'8px 18px', fontFamily:'inherit', fontSize:13, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                            {completada ? 'Review' : 'Start'}
                          </button>
                        )}
                        {!desbloqueada && (
                          <div style={{ fontSize:11, color:'#9CA3AF', textAlign:'right' }}>Complete<br/>previous lesson</div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
